import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CoverageLineDto,
  PlanRiderDto,
} from 'enrollment/queries/useBenefitPlansQuery/types';
import {
  MobileRiderBenefitContainerStyled,
  RiderBenefitContainerStyled,
  RiderLinesContainerStyled,
} from './RiderBenefitsStyled';
import {
  DRAWER_ON_BENEFIT_MOBILE_CARD,
  getRiderCoverage,
  RiderBenefitDrawer,
  RiderBenefitItem,
  TotalCostBanner,
} from '.';
import { RiderCoverageDto } from 'enrollment/queries/useBenefitCategoryQuery/types';
import { PSBannerWrapper } from '../Shared';

export interface RiderBenefitsProps {
  riderInfo: PlanRiderDto[];
  riderCoverages: RiderCoverageDto[];
  planPremium: number;
  electedRiders: number[];
  orgPlanYearId: number;
  setElectedRiders: Dispatch<SetStateAction<number[]>>;
  displayCheckbox?: boolean;
  smallMobileMargins?: boolean;
  inlineRiders?: boolean;
  coverageLine?: CoverageLineDto;
  setTotalCostRiders?: Dispatch<SetStateAction<number>>;
  hideEmptyCost?: boolean;
}

export const RiderBenefits = ({
  riderInfo = [],
  riderCoverages,
  planPremium,
  electedRiders,
  orgPlanYearId,
  setElectedRiders,
  displayCheckbox = false,
  smallMobileMargins = false,
  inlineRiders = false,
  coverageLine,
  setTotalCostRiders,
  hideEmptyCost = false,
}: RiderBenefitsProps) => {
  const { t } = useTranslation('common');
  const isMobile = window.innerWidth < 744;

  const filteredRiderInfo = riderInfo.filter((rider) => rider.elect_with_plan);
  const coverageLineId = coverageLine?.id || undefined;

  const [totalCost, setTotalCost] = useState(planPremium);
  const [costMap, setCostMap] = useState<Map<number, number>>(
    new Map<number, number>()
  ); // key: riderId, value: cost

  useEffect(() => {
    let newVal = planPremium;
    electedRiders.forEach((riderId) => {
      const cost = costMap.get(riderId);
      if (cost) {
        newVal += cost;
      }
    });

    setTotalCost(newVal);
    if (setTotalCostRiders) {
      setTotalCostRiders(newVal - planPremium);
    }
  }, [planPremium, electedRiders, costMap, setTotalCostRiders]);

  useEffect(() => {
    if (riderInfo.length > 0) {
      const filteredInfo = riderInfo.filter((rider) => rider.elect_with_plan);
      const newCostMap = new Map<number, number>();
      const newElectedRiders: number[] = [];
      filteredInfo.forEach((rider) => {
        const riderCov = getRiderCoverage(
          rider,
          riderCoverages,
          coverageLineId
        );
        if (riderCov) {
          newElectedRiders.push(rider.rider_benefit_id);
          newCostMap.set(
            rider.rider_benefit_id,
            Number(riderCov.req_subscriber_premium)
          );
        }
      });

      setElectedRiders(newElectedRiders);
      setCostMap(newCostMap);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [riderInfo, setElectedRiders]);

  if (filteredRiderInfo.length < 1) {
    return null;
  }

  if (!isMobile) {
    return (
      <RiderBenefitContainerStyled
        $hideBorder={inlineRiders}
        data-testid="rider-benefits-container"
      >
        <p className="semiBold">{t('enrollment.additionalRiders')}</p>
        <RiderLinesContainerStyled>
          {filteredRiderInfo.map((rider, index) => {
            return (
              <RiderBenefitItem
                riderInfo={rider}
                riderCoverage={getRiderCoverage(
                  rider,
                  riderCoverages,
                  coverageLineId
                )}
                orgPlanYearId={orgPlanYearId}
                electedRiders={electedRiders}
                setElectedRiders={setElectedRiders}
                displayCheckbox={displayCheckbox}
                costMap={costMap}
                setCostMap={setCostMap}
                key={index + Number(coverageLineId)}
                disabled={coverageLine && !coverageLine?.in_current_plan_year}
                coverageLineId={coverageLineId}
              />
            );
          })}
        </RiderLinesContainerStyled>
        <TotalCostBanner cost={totalCost} hideEmptyCost={hideEmptyCost} />
      </RiderBenefitContainerStyled>
    );
  } else {
    return (
      <MobileRiderBenefitContainerStyled
        data-testid="rider-benefits-container"
        className={smallMobileMargins ? 'mx-4' : 'mx-5'}
      >
        {electedRiders.length === 0 && (
          <PSBannerWrapper
            message={t('enrollment.mobileRiderStatement')}
            level="info"
            icon={true}
            extraClass="mb-3 w-100 mt-n4"
          />
        )}
        <div className="d-flex justify-content-center">
          <RiderBenefitDrawer
            riders={filteredRiderInfo}
            riderCoverages={riderCoverages}
            orgPlanYearId={orgPlanYearId}
            drawerOrigin={DRAWER_ON_BENEFIT_MOBILE_CARD}
            electedRiders={electedRiders}
            setElectedRiders={setElectedRiders}
            costMap={costMap}
            setCostMap={setCostMap}
          />
        </div>
        {electedRiders.length > 0 && (
          <div className="mt-2">
            <TotalCostBanner cost={totalCost} hideEmptyCost={hideEmptyCost} />
          </div>
        )}
      </MobileRiderBenefitContainerStyled>
    );
  }
};
